import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  IconArrowRight,
  IconInfoCircle,
  IconTarget,
  type TablerIcon,
} from '@tabler/icons-react';
import { Box, Button, Flex, Text } from '~/design-system';
import type { ImporterDescriptor } from '../../descriptors';

type MatchedCardProps = {
  matches: ImporterDescriptor[];
  onBrowseAll: () => void;
};

export default function MatchedCard({ matches, onBrowseAll }: MatchedCardProps) {
  if (matches.length === 0) return null;
  const multiple = matches.length > 1;

  return (
    <Box
      sx={{
        borderRadius: 10,
        border: '1px solid #c7d2fe',
        backgroundColor: 'white',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <Flex
        sx={{
          alignItems: 'center',
          gap: 1,
          padding: '8px 12px',
          backgroundColor: '#eef2ff',
          borderBottom: '1px solid #e0e7ff',
          color: '#4338ca',
        }}
      >
        <IconTarget size={14} stroke={1.75} />
        <Text
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            // @ts-ignore — theme-ui letterSpacing/textTransform values
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {multiple ? <Trans>Top matches</Trans> : <Trans>Matched</Trans>}
        </Text>
      </Flex>

      {matches.map((match, index) => (
        <MatchRow
          key={match.id}
          match={match}
          isPrimary={index === 0}
          showDivider={index > 0}
        />
      ))}

      <Flex
        sx={{
          alignItems: 'center',
          gap: 1,
          padding: '8px 12px',
          borderTop: '1px solid #f3f4f6',
          backgroundColor: '#fafafa',
          color: '#6b7280',
          fontSize: '12px',
        }}
      >
        <IconInfoCircle size={14} stroke={1.75} />
        <Text sx={{ fontSize: '12px', color: '#6b7280' }}>
          <Trans>Not what you meant?</Trans>
        </Text>
        {/* @ts-ignore — Box typing doesn't surface button-element props */}
        <Box
          as="button"
          type="button"
          onClick={onBrowseAll}
          sx={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: '#2563eb',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          <Trans>Browse all options</Trans>
        </Box>
      </Flex>
    </Box>
  );
}

type MatchRowProps = {
  match: ImporterDescriptor;
  isPrimary: boolean;
  showDivider: boolean;
};

function MatchRow({ match, isPrimary, showDivider }: MatchRowProps) {
  const { i18n } = useLingui();
  const history = useHistory();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const Icon = match.icon as unknown as TablerIcon;

  function onUseThis() {
    history.push(`/${workspaceId}/${match.id}`);
  }

  return (
    <Flex
      sx={{
        alignItems: 'center',
        gap: 3,
        padding: 3,
        borderTop: showDivider ? '1px solid #f3f4f6' : undefined,
      }}
    >
      <Flex
        sx={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: '#dbeafe',
          color: '#2563eb',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={20} stroke={1.75} />
      </Flex>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text
          sx={{
            fontSize: isPrimary ? '15px' : '14px',
            fontWeight: 600,
            color: '#111827',
            display: 'block',
            marginBottom: 1,
          }}
        >
          {i18n._(match.title)}
        </Text>
        <Text
          sx={{
            fontSize: '13px',
            color: '#6b7280',
            lineHeight: 1.45,
          }}
        >
          {i18n._(match.whenToUse)}
        </Text>
      </Box>
      {isPrimary ? (
        <Button variant="primary" onClick={onUseThis} sx={{ flexShrink: 0 }}>
          <Flex sx={{ alignItems: 'center', gap: 1 }}>
            <Trans>Use this</Trans>
            <IconArrowRight size={16} stroke={2} />
          </Flex>
        </Button>
      ) : (
        // @ts-ignore — Box typing doesn't surface button-element props
        <Box
          as="button"
          type="button"
          onClick={onUseThis}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #e5e7eb',
            background: 'white',
            color: '#2563eb',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': {
              borderColor: '#c7d2fe',
              backgroundColor: '#f5f9ff',
            },
          }}
        >
          <Trans>Use this</Trans>
          <IconArrowRight size={14} stroke={2} />
        </Box>
      )}
    </Flex>
  );
}
